import { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { compactNumber } from "@/lib/format";

const COLORS = ["#0047FF", "#F53B16", "#F59E0B", "#10B981", "#F97316", "#0F172A", "#8B5CF6", "#06B6D4"];
const MONTH_ORDER = ["JAN", "FEB", "MAR", "MARET", "APR", "APRIL", "MEI", "JUN", "JUNI", "JUL", "JULI", "AGST", "AUG", "SEP", "SEPT", "OKT", "OCT", "NOV", "DES", "DEC"];

function monthKey(v) {
  if (!v) return "99";
  const s = String(v).toUpperCase().slice(0, 3);
  const idx = MONTH_ORDER.findIndex((m) => m.startsWith(s));
  return idx >= 0 ? String(idx).padStart(2, "0") : "99";
}

export default function DataChart({ module, records }) {
  const chart = module.chart;
  const data = useMemo(() => {
    if (!records || records.length === 0) return { series: [], type: chart.type };

    if (chart.series && chart.type === "line") {
      // group by x, series -> value
      const groups = {};
      const seriesSet = new Set();
      records.forEach((r) => {
        const x = r.data?.[chart.x] ?? "?";
        const s = r.data?.[chart.series] ?? "-";
        const v = Number(r.data?.[chart.y]) || 0;
        seriesSet.add(s);
        groups[x] = groups[x] || { [chart.x]: x };
        groups[x][s] = (groups[x][s] || 0) + v;
      });
      const series = Object.values(groups).sort((a, b) =>
        monthKey(a[chart.x]).localeCompare(monthKey(b[chart.x]))
      );
      return { series, seriesKeys: Array.from(seriesSet), type: chart.type };
    }

    // simple aggregation
    const agg = {};
    records.forEach((r) => {
      const x = r.data?.[chart.x] ?? "?";
      if (chart.agg === "count") {
        agg[x] = (agg[x] || 0) + 1;
      } else {
        const v = Number(r.data?.[chart.y]) || 0;
        agg[x] = (agg[x] || 0) + v;
      }
    });
    let series = Object.keys(agg).map((k) => ({ [chart.x]: k, value: agg[k] }));
    // sort: if x looks like month, order it; else by value desc
    const anyMonth = series.some((s) => monthKey(s[chart.x]) !== "99");
    if (anyMonth) {
      series.sort((a, b) => monthKey(a[chart.x]).localeCompare(monthKey(b[chart.x])));
    } else {
      series.sort((a, b) => b.value - a.value);
    }
    return { series, type: chart.type };
  }, [records, chart]);

  const yFmt = (v) => compactNumber(v);
  const tipFmt = (v) =>
    chart.y === "net_penjualan" || chart.y === "total_transaksi"
      ? "Rp " + Number(v).toLocaleString("id-ID")
      : Number(v).toLocaleString("id-ID");

  return (
    <div className="bg-white border border-slate-200 p-6" data-testid="module-chart">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="mono text-[11px] uppercase tracking-widest text-slate-400">
            Visualisasi
          </div>
          <h3 className="font-display font-black text-lg tracking-tight">
            {chart.title}
          </h3>
        </div>
        <div className="mono text-[11px] uppercase tracking-widest text-slate-400">
          {chart.type}
        </div>
      </div>

      {data.series.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-slate-400 text-sm">
          Belum ada data untuk divisualisasikan.
        </div>
      ) : (
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            {data.type === "line" && data.seriesKeys ? (
              <LineChart data={data.series}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey={chart.x} stroke="#64748b" tick={{ fontSize: 12 }} />
                <YAxis stroke="#64748b" tick={{ fontSize: 12 }} tickFormatter={yFmt} />
                <Tooltip
                  formatter={tipFmt}
                  contentStyle={{ background: "#0f172a", border: 0, color: "#fff", borderRadius: 2 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {data.seriesKeys.map((k, i) => (
                  <Line
                    key={k}
                    type="monotone"
                    dataKey={k}
                    stroke={COLORS[i % COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                ))}
              </LineChart>
            ) : data.type === "pie" ? (
              <PieChart>
                <Pie
                  data={data.series.map((s) => ({ name: s[chart.x], value: s.value }))}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={110}
                  innerRadius={60}
                  paddingAngle={2}
                >
                  {data.series.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={tipFmt}
                  contentStyle={{ background: "#0f172a", border: 0, color: "#fff", borderRadius: 2 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            ) : (
              <BarChart data={data.series}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey={chart.x} stroke="#64748b" tick={{ fontSize: 12 }} />
                <YAxis stroke="#64748b" tick={{ fontSize: 12 }} tickFormatter={yFmt} />
                <Tooltip
                  formatter={tipFmt}
                  contentStyle={{ background: "#0f172a", border: 0, color: "#fff", borderRadius: 2 }}
                />
                <Bar dataKey="value" fill="#0047FF" />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
